import React, { useRef, useEffect, forwardRef } from 'react'
import { useUpdateRef } from '../../common'

/**
 * @template Item
 * @param {import('../interfaces').InputRendererProps<Item>} props
 * @param {import('react').Ref<HTMLElement>} ref
 * @returns {JSX.Element}
 */
function ghostRenderer (
  {
    value,
    focusedSuggestionValue,
    suggestions,
    getSuggestionValue,
    type,
    placeholder,
    isError,
    isOpen,
    isDisabled,
    isFocused,
    noAutoSuggestion,
    ...rest
  },
  ref
) {
  /** @type {import('react').RefObject<HTMLElement>} */
  const inputRef = useRef()
  /** @type {import('react').RefObject<HTMLElement>} */
  const ghostRef = useRef()
  const prevIsFocusedRef = useRef(isFocused)
  /** @type {import('react').RefObject<EventListenerOptions>} */
  const eventOptionsRef = useRef()
  const ghost =
    isDisabled || !isOpen
      ? ''
      : getGhost(value, focusedSuggestionValue, suggestions, getSuggestionValue, noAutoSuggestion)

  const inputProps = {
    ...rest,
    value,
    placeholder: !ghost ? placeholder : null,
    ref: inputRef
  }
  if (!ghost) {
    ghostRef.current = null
  }

  useUpdateRef(ref, inputRef)
  useEffect(checkForPassiveEvents, [])
  useEffect(componentWillReceiveProps, [isFocused])
  useEffect(componentWillMount, [])

  return (
    <span className='zb-lookup-ghost-renderer'>
      {!!ghost && (
        <span className='zb-lookup-ghost-renderer-ghost' ref={ghostRef}>
          <span className='zb-lookup-ghost-renderer-ghost-text'>{ghost}</span>
        </span>
      )}

      <input {...inputProps} />
    </span>
  )

  function checkForPassiveEvents () {
    try {
      var options = {
        get passive () {
          eventOptionsRef.current = { passive: true }
        }
      }

      window.addEventListener('test', options, options)
      window.removeEventListener('test', options, options)
    } catch (err) {}
  }

  function componentWillReceiveProps () {
    const wasFocused = prevIsFocusedRef.current
    if (!wasFocused && isFocused) {
      inputRef.current.focus()
    } else if (wasFocused && !isFocused) {
      inputRef.current.blur()
    }
  }

  function handleGhostEvent () {
    positionGhost()
  }

  function positionGhost () {
    const element = inputRef.current
    const ghost = ghostRef.current
    if (ghost && element) {
      ghost.style.width = `${element.clientWidth}px`
      ghost.style.boxSizing = window.getComputedStyle(element).boxSizing
      ghost.firstChild.style.left = `${-element.scrollLeft}px`
    }
  }

  function componentWillMount () {
    const element = inputRef.current
    const eventOptions = eventOptionsRef.current
    ghostRenderer.ghostEvents.forEach(event =>
      element.addEventListener(event, handleGhostEvent, eventOptions)
    )

    positionGhost()

    return removeEventListeners

    function removeEventListeners () {
      ghostRenderer.ghostEvents.forEach(event =>
        element.removeEventListener(event, handleGhostEvent, eventOptions)
      )
    }
  }
}

ghostRenderer.ghostEvents = [
  'change',
  'click',
  'cut',
  'drag',
  'dragleave',
  'dragover',
  'drop',
  'focus',
  'keydown',
  'keyup',
  'mousedown',
  'mouseenter',
  'mouseleave',
  'mouseup',
  'paste',
  'resize',
  'scroll',
  'select',
  'wheel'
]

/**
 * @template Item
 * @param {string} value
 * @param {string} focusedSuggestionValue
 * @param {Item[]} suggestions
 * @param {import('../interfaces').SuggestionValueGetter<Item>} getSuggestionValue
 */
function getGhost (
  value,
  focusedSuggestionValue,
  suggestions,
  getSuggestionValue,
  noAutoSuggestion
) {
  if (focusedSuggestionValue) {
    return focusedSuggestionValue.indexOf(value) === 0
      ? focusedSuggestionValue
      : ''
  }

  if (suggestions.length) {
    const firstSuggestionValue = getSuggestionValue(suggestions[0])

    return value && firstSuggestionValue.indexOf(value) === 0
      ? noAutoSuggestion ? value : firstSuggestionValue
      : ''
  }

  return ''
}

const GhostRenderer = forwardRef(ghostRenderer)

GhostRenderer.displayName = 'GhostRenderer'

export { GhostRenderer }

export default function (props) {
  return <GhostRenderer {...props} />
}
